import React from 'react'
import axios from 'axios'
import { setUser } from '@sentry/nextjs'

import useGoogleAuthorize, {
  useGoogleIdentityServicesAuthorize,
} from 'utils/hooks/useGoogleAuthorize'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import {
  APP_LOGIN_STORAGE_KEY,
  GURU_TOKEN_GOOGLE_OAUTH_CONFIG,
} from 'configs/auth'
import { baseUrl } from 'configs/api'

import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import type { AuthContextType, UseAuthProvider } from './types'
import type { Session } from 'types/auth'
import type { ApiStatus } from 'types/api'

const AuthContextDefaultValue: AuthContextType = {
  session: null,
  login: () => {},
  loginScriptIsLoaded: false,
  logout: () => {},
  validateTokenError: null,
  lastUsedEmailDomain: null,
  error: null,
}

export const AuthContext = React.createContext<AuthContextType>(
  AuthContextDefaultValue
)
AuthContext.displayName = 'AuthContext'

export function useAuthProvider(immediate: boolean = true): UseAuthProvider {
  const {
    run,
    session,
    status,
    error: validateTokenError,
    resetSession,
  } = useValidateToken()

  const [error, setError] = React.useState(null)
  const [lastUsedEmailDomain, setLastUsedEmailDomain] = React.useState(null)

  React.useEffect(() => {
    if (immediate) {
      run(null)
    }
    // eslint-disable-next-line
  }, [])

  const { authorize: login, loaded: loginScriptIsLoaded } = (
    process.env.NEXT_PUBLIC_GOOGLE_IDENTITY_SERVICES_ENABLED === 'true'
      ? useGoogleIdentityServicesAuthorize
      : useGoogleAuthorize
  )({
    ...GURU_TOKEN_GOOGLE_OAUTH_CONFIG,
    onSuccess({ idToken, emailDomain }) {
      run(idToken)
      setLastUsedEmailDomain(emailDomain)
    },
    onFailure(loginError) {
      if (loginError !== 'popup_closed_by_user') {
        new Error(
          // Part of this string is used for new relic alert.
          // Double check with new relic policy dashboard if you want to modify
          `${loginError} (Login failed due to cannot get idToken from Google)`
        )
      }
      setError(loginError)
    },
  })

  const logout = () => {
    resetSession()
  }

  return {
    session,
    login,
    loginScriptIsLoaded,
    logout,
    status,
    error,
    validateTokenError,
    resetSession,
    lastUsedEmailDomain,
  }
}

export function AuthProvider(props) {
  const {
    session,
    login,
    loginScriptIsLoaded,
    logout,
    status,
    error,
    validateTokenError,
    lastUsedEmailDomain,
  } = useAuthProvider()

  const value = React.useMemo(
    () => ({
      session,
      login,
      loginScriptIsLoaded,
      logout,
      error,
      validateTokenError,
      lastUsedEmailDomain,
    }),
    [
      session,
      login,
      loginScriptIsLoaded,
      logout,
      error,
      validateTokenError,
      lastUsedEmailDomain,
    ]
  )

  if (['idle', 'loading'].includes(status)) {
    return null
  } else {
    return <AuthContext.Provider value={value} {...props} />
  }
}

function useValidateToken() {
  const [storedSession, setSession, removeSession] = useLocalStorage(
    APP_LOGIN_STORAGE_KEY,
    null
  )
  const [status, setStatus] = React.useState<ApiStatus>('idle')
  const [error, setError] = React.useState(null)

  const run = async (idToken: string | null) => {
    setStatus('loading')

    try {
      const session: Session = await validateToken(idToken)
      if (!!session) {
        setSession(session)
      }

      const userId = storedSession?.user?.id ?? session?.user?.id ?? null
      if (userId) {
        setUser({ id: userId })
      }

      setStatus('success')
    } catch (err) {
      setError(err)
      setStatus('error')
    }
  }

  const resetSession = () => {
    setUser(null)
    removeSession()
    setStatus('idle')
  }

  return {
    run,
    session: storedSession,
    status,
    error,
    resetSession,
  }
}

async function validateToken(idToken: string | null): Promise<Session | null> {
  if (idToken) {
    try {
      const axiosConfig: AxiosRequestConfig = {
        url: `${baseUrl}/teachers/v1alpha2/login`,
        method: 'POST',
        data: {
          grantType: 'GOOGLE',
          token: idToken,
        },
      }
      const { data } = (await axios(axiosConfig)) as AxiosResponse<{
        data: Session
      }>
      return data.data
    } catch (error) {
      new Error(
        // Part of this string is used for new relic alert.
        // Double check with new relic policy dashboard if you want to modify
        `${error} (Login failed due to cannot exchange idToken to guruToken)`
      )
      throw error
    }
  } else {
    return null
  }
}
